// Records WHAT changed in the writing box and WHEN, never which keys were pressed.
// Event format (must match backend/authorship.py): [ms since previous event, kind, characters]
export const LETTER = 0, SPACE = 1, PUNCT = 2, DELETE = 3, PASTE = 4, DROP = 5, DICTATION = 6, OTHER_INSERT = 7;

const MAX_EVENTS = 30000;
const MAX_GAP_MS = 3600000;
const MAX_CHARS = 20000;
const PREDICTED_WORD_MAX = 12;

export const charKind = (ch) => (/\s/.test(ch) ? SPACE : /[\p{L}\p{N}]/u.test(ch) ? LETTER : PUNCT);

export function createTypingRecorder() {
  const st = { events: [], lastT: null, lastLen: 0, pendingPaste: false, pasteCount: 0 };

  const push = (dt, kind, n) => {
    if (n >= 1 && st.events.length < MAX_EVENTS) st.events.push([dt, kind, Math.min(n, MAX_CHARS)]);
  };
  const tick = (now) => {
    const dt = st.lastT == null ? 0 : Math.min(Math.max(0, Math.round(now - st.lastT)), MAX_GAP_MS);
    st.lastT = now;
    return dt;
  };

  return {
    state: st,
    get events() { return st.events; },
    get pasteCount() { return st.pasteCount; },
    // older keyboards do not say what kind of change it was; the paste event tells us
    markPaste() { st.pendingPaste = true; },
    // put back a saved draft's timeline after a reload
    restore(events, textLength) {
      st.events = Array.isArray(events) ? events : [];
      st.lastLen = textLength;
      st.lastT = null;
      st.pendingPaste = false;
      st.pasteCount = 0;
    },
    // keep length in step when monitoring is off
    setLength(n) { st.lastLen = n; },
    // call for every change in the box; the change in length is what we trust
    record(ne, value, now = performance.now()) {
      const dt = tick(now);
      const delta = value.length - st.lastLen;
      st.lastLen = value.length;
      const type = (ne && ne.inputType) || (st.pendingPaste ? 'insertFromPaste' : '');
      st.pendingPaste = false;
      const data = (ne && ne.data) || '';
      if (type.startsWith('delete')) { push(dt, DELETE, Math.max(1, -delta)); return; }
      if (type === 'insertFromPaste') { st.pasteCount += 1; push(dt, PASTE, Math.max(1, delta)); return; }
      if (type === 'insertFromDrop') { push(dt, DROP, Math.max(1, delta)); return; }
      if (type === 'insertLineBreak' || type === 'insertParagraph') { push(dt, SPACE, Math.max(1, delta)); return; }
      const typedLike = type === 'insertText' || type === 'insertCompositionText'
        || type === 'insertFromComposition' || type === 'insertReplacementText';
      if (typedLike) {
        if (delta <= 0) {                       // typing over a selection
          push(dt, DELETE, 1 - delta);
          push(0, charKind(data.slice(-1) || 'a'), 1);
        } else if (delta === 1) {
          push(dt, charKind(data.slice(-1) || 'a'), 1);
        } else if (delta <= PREDICTED_WORD_MAX) { // a predicted word from a phone keyboard
          push(dt, LETTER, delta);
        } else {
          push(dt, OTHER_INSERT, delta);
        }
        return;
      }
      if (delta > 0) push(dt, OTHER_INSERT, delta);
      else if (delta < 0) push(dt, DELETE, -delta);
    },
    // text inserted by the microphone button
    addDictation(newLength, now = performance.now()) {
      const dt = tick(now);
      push(dt, DICTATION, Math.max(1, newLength - st.lastLen));
      st.lastLen = newLength;
    },
  };
}
